import { useMemo, useState } from "react";
import {
  AlertOctagon,
  AlertTriangle,
  BellRing,
  CheckCheck,
  Hourglass,
  Inbox,
  ShieldAlert,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { hasRole } from "../utils/auth";
import { useNotifications } from "../context/NotificationContext";
import NotificationItem from "../components/Notifikasi/NotificationItem";
import { NOTIF_TYPES } from "../utils/notificationEngine";

const FILTERS_BASE = [
  { key: "ALL", label: "Semua", icon: BellRing },
  { key: "UNREAD", label: "Belum Dibaca", icon: AlertTriangle },
  { key: "PERINGATAN", label: "Peringatan", icon: AlertTriangle },
  { key: "TERLAMBAT", label: "Terlambat", icon: AlertOctagon },
  { key: "STAGNASI", label: "Stagnasi", icon: Hourglass },
];

export default function Notifikasi() {
  const { user } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead, refresh } = useNotifications();
  const [filter, setFilter] = useState("ALL");

  const filters = useMemo(() => {
    if (hasRole(user, "IRBAN")) {
      return [...FILTERS_BASE, { key: "ESKALASI_AUDIT", label: "Eskalasi Audit", icon: ShieldAlert }];
    }
    return FILTERS_BASE;
  }, [user]);

  const counts = useMemo(() => {
    const c = {
      ALL: notifications.length,
      UNREAD: notifications.filter((n) => !n.read).length,
      PERINGATAN: notifications.filter((n) => n.type === NOTIF_TYPES.PERINGATAN).length,
      TERLAMBAT: notifications.filter((n) => n.type === NOTIF_TYPES.TERLAMBAT).length,
      STAGNASI: notifications.filter((n) => n.type === NOTIF_TYPES.STAGNASI).length,
      ESKALASI_AUDIT: notifications.filter((n) => n.type === NOTIF_TYPES.ESKALASI_AUDIT).length,
    };
    return c;
  }, [notifications]);

  const visible = useMemo(() => {
    if (filter === "ALL") return notifications;
    if (filter === "UNREAD") return notifications.filter((n) => !n.read);
    return notifications.filter((n) => n.type === filter);
  }, [notifications, filter]);

  return (
    <div className="space-y-5" data-testid="notifikasi-page">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-5 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Operasional
          </div>
          <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
            Notifikasi
          </h1>
          <p className="mt-1 text-[13px] text-slate-500">
            Peringatan otomatis untuk sub-kegiatan yang memerlukan tindak lanjut.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            data-testid="notif-refresh"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[12px] font-semibold text-slate-700 hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
          >
            Refresh Engine
          </button>
          {unreadCount > 0 ? (
            <button
              type="button"
              onClick={markAllAsRead}
              data-testid="notif-mark-all-page"
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#1e3a5f] px-3 text-[12px] font-semibold text-white hover:bg-[#152a46]"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Tandai Semua Dibaca
            </button>
          ) : null}
        </div>
      </div>

      {/* Filter pills */}
      <div
        data-testid="notif-filter-bar"
        className="flex flex-wrap items-center gap-2"
      >
        {filters.map((f) => {
          const Icon = f.icon;
          const active = filter === f.key;
          const count = counts[f.key] || 0;
          return (
            <button
              type="button"
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`notif-filter-${f.key}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                active
                  ? "border-[#1e3a5f] bg-[#1e3a5f] text-white"
                  : "border-gray-200 bg-white text-slate-700 hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
              }`}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
              {f.label}
              <span
                className={`ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold ${
                  active ? "bg-white/20 text-white" : "bg-gray-100 text-slate-600"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {visible.length === 0 ? (
        <div
          data-testid="notif-empty"
          className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-6 py-16 text-center"
        >
          <Inbox className="h-6 w-6 text-slate-400" strokeWidth={1.8} />
          <h3 className="mt-3 font-display text-base font-semibold text-slate-900">
            Tidak ada notifikasi
          </h3>
          <p className="mt-1 text-[12px] text-slate-500">
            Tidak ada notifikasi pada kategori ini. Semua aman!
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="notif-list">
          {visible.map((n) => (
            <NotificationItem key={n.id} notif={n} onMarkRead={markAsRead} />
          ))}
        </div>
      )}
    </div>
  );
}
